export const REGEX_PATTERNS = {
    NAME: /^[a-zA-Z0-9\-_. /]*$/,
    EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
    CONTACT: /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/i,
    DESIGNATION_LEVEL_NAME: /^[a-zA-Z0-9\-_. /]+$/,
};

export const COMPONENT_FIELD_LIMITS = {
    employee: {
        firstName: 20,
        lastName: 20,
        empId: 15,
        nic: 15,
        address: 500,
        personalEmail: 320,
        personalContact1: 15,
        personalContact2: 15,
        passport: 15,
    },
    rank: {
        rankName: 50,
        rankDescription: 100,
    },
    visitProfile: {
        name: 255,
    },
    permissionGroup: {
        name: 50,
        description: 100,
    },
    officeLocation: {
        address: 500,
    },
    team: {
        name: 20,
        description: 100,
    },
    product: {
        name: 100,
        description: 300,
    },
    designation: {
        name: 50,
        description: 100,
    },
    designationLevel: {
        name: 20,
        description: 100,
    },
    department: {
        name: 20,
        description: 100,
    },
    doctor: {
        name: 40,
    },
    doctordesignation: {
        name: 50,
        description: 100,
    },
    doctorSpecialization: {
        name: 50,
        description: 100,
    },
    practicelocation: {
        name: 100,
        address: 200,
    },
    locationtype: {
        name: 50,
        description: 200,
    },
    employeementstatus: {
        name: 50,
        description: 200,
    },
    subtask: {
        name: 20,
        description: 100,
    },
    taskpriority: {
        name: 20,
        description: 100,
    },
    tasktype: {
        name: 20,
        description: 100,
    },
    city: {
        name: 100,
        shortName: 10,
    },
    role: {
        name: 100,
        description: 200,
    },
    territory: {
        name: 100,
    },
};

export const COMPONENT_REQUIRED_FIELDS = {
    employee: ["firstName", "lastName", "personalEmail", "personalContact1", "employeeRoles", "employmentStatus", "team", "reportsTo",
        "department", "jobLocation", "city", "address", 'gender', "dateOfJoining", "designation"
    ],
    permissionGroup: ["name", "description", "permissionIds"],
    officeLocation: ["contact1", "address", "cityId"],
    rank: ["rankName", "rankDescription"],
    visitProfile: ["name", "city"],
    team: ['name', 'description'],
    subtask: ['name', 'description'],
    taskpriority: ['name', 'description', 'color'],
    product: ['name', 'description'],
    designation: ['name', 'description', 'shortName', 'levelId'],
    designationLevel: ['name', 'description'],
    department: ['name', 'description'],
    employeementstatus: ['name', 'description'],
    locationtype: ['name', 'description'],
    doctordesignation: ['name', 'description'],
    doctorSpecialization: ['name', 'description'],
    practicelocation: ['name', 'address', 'city', 'type'],
    doctor: ['name', 'specialization', 'rank', 'customerRankId', 'city'],
    city: ['name', 'shortName', 'countryId'],
    role: ['name', 'description', 'roleGroup'],
    tasktype: ['name', 'description'],
    territory: ['name', 'cityId', 'practiceLocationIds'],
};

export const ERROR_MESSAGES = {
    REQUIRED: (field) => `${field} is required!`,
    MAX_LENGTH: (field, limit) => `${field} must not exceed ${limit} characters!`,
    INVALID_CHAR: "Special characters are not allowed!",
    INVALID_EMAIL: "Please enter a valid email!",
    INVALID_CONTACT: "Contact is invalid!",
    LEVEL_NAME_REQUIRED: "Level name is required!",
    Permission_Msg:"At least select one permission",
    COUNTRY_NAME_REQUIRED: "Country name is required!",
};

const formatFieldName = (field) => {
    return field
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const validateField = (errors, values, field, fieldName, limitKey, limits, requiredFields) => {
    const value = values[field] ? String(values[field]).trim() : "";

    if (requiredFields.includes(field) && !value) {
        errors[field] = ERROR_MESSAGES.REQUIRED(fieldName);
    } else if (value) {
        if (limits[limitKey] && value.length > limits[limitKey]) {
            errors[field] = ERROR_MESSAGES.MAX_LENGTH(fieldName, limits[limitKey]);
        }
        if (REGEX_PATTERNS[field.toUpperCase()] && !REGEX_PATTERNS[field.toUpperCase()].test(value)) {
            errors[field] = ERROR_MESSAGES.INVALID_CHAR;
        }
    }
};

export const validateFields = (values, componentType) => {
    const errors = {};
    const limits = COMPONENT_FIELD_LIMITS[componentType] || {};
    const requiredFields = COMPONENT_REQUIRED_FIELDS[componentType] || [];

    requiredFields.forEach((field) => {
        const value = values[field];
        if (field === "permissionIds") {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                errors[field] = ERROR_MESSAGES.Permission_Msg;
            }
        } else if (field === "levelId") {
            if (!value) {
                errors[field] = ERROR_MESSAGES.LEVEL_NAME_REQUIRED;
            }
        } else if (field === "countryId") {
            if (!value) {
                errors[field] = ERROR_MESSAGES.COUNTRY_NAME_REQUIRED;
            }
        } else {
            if (!value || (Array.isArray(value) && value.length === 0) || String(value).trim() === "") {
                errors[field] = ERROR_MESSAGES.REQUIRED(formatFieldName(field));
            }
        }
    });

      if (componentType === 'employee') {
        if (!values.isEmpIdAutoGenerated && (!values.empId || String(values.empId).trim() === '')) {
            errors.empId = ERROR_MESSAGES.REQUIRED(formatFieldName("empId"));
        }
    }

    Object.keys(limits).forEach((field) => {
        validateField(errors, values, field, formatFieldName(field), field, limits, requiredFields);
    });

    if (values.personalEmail && !REGEX_PATTERNS.EMAIL.test(String(values.personalEmail))) {
        errors.personalEmail = ERROR_MESSAGES.INVALID_EMAIL;
    }

    ["personalContact1", "personalContact2", "contact1"].forEach((contactField) => {
        if (values[contactField] && !REGEX_PATTERNS.CONTACT.test(String(values[contactField]))) {
            errors[contactField] = ERROR_MESSAGES.INVALID_CONTACT;
        }
    });   
    if (componentType === "designationLevel" && values.name && !REGEX_PATTERNS.DESIGNATION_LEVEL_NAME.test(values.name)) {
        errors.name = ERROR_MESSAGES.INVALID_CHAR;
    }

    if (componentType === "visitProfile" && values.rows && values.rows.length > 0) {
        errors.rows = values.rows.map((row) => {
            const rowErrors = {};
            if (!row.practiceLocation) {
                rowErrors.practiceLocation = ERROR_MESSAGES.REQUIRED("Practice Location");
            }
            if (!row.doctors || row.doctors.length === 0) {
                rowErrors.doctors = ERROR_MESSAGES.REQUIRED("At least one Doctor");
            }
            return Object.keys(rowErrors).length > 0 ? rowErrors : null;
        });

        if (errors.rows.every(error => error === null)) {
            delete errors.rows;
        }
    }

    if (componentType === "doctor" && values.rows && values.rows.length > 0) {
        errors.rows = values.rows.map((row) => {
            const rowErrors = {};
            if (!row.cityId) {
                rowErrors.cityId = ERROR_MESSAGES.REQUIRED("City");
            }
            if (!row.practiceLocationIds || row.practiceLocationIds.length === 0) {
                rowErrors.practiceLocationIds = ERROR_MESSAGES.REQUIRED("At least one Practice Location");
            }
            return Object.keys(rowErrors).length > 0 ? rowErrors : null;
        });

        if (errors.rows.every(error => error === null)) {
            delete errors.rows;
        }
    }

    return errors;
};
